import { useCallback, useEffect, useMemo, useState } from 'react'
import { Link, useLocation, useNavigate, useSearchParams } from 'react-router-dom'
import { Trans, useTranslation } from 'react-i18next'
import { useAuth } from '../auth/AuthContext'
import { Icon } from '../components/Icon'
import { FeatRow } from '../components/FeatRow'
import { useCatalog } from '../hooks/useCatalog'
import { useCountriesMeta } from '../hooks/useCountriesMeta'
import {
  COUNTRIES,
  REGIONAL_PLANS_META,
  fromPrice,
} from '../data/catalog'
import { languageFromPath, withLang } from '../i18n/i18n'
import { useDocumentMeta } from '../i18n/useDocumentMeta'

type DestTab = 'local' | 'regional'

export default function Destinations() {
  const { plans, loading } = useCatalog()
  const countriesMeta = useCountriesMeta()
  const { user } = useAuth()
  const navigate = useNavigate()
  const location = useLocation()
  const { t } = useTranslation('destinations')
  const { t: tCommon } = useTranslation('common')
  const lang = languageFromPath(location.pathname)
  useDocumentMeta({ title: t('seo.title'), description: t('seo.description') })
  const [q, setQ] = useState('')
  const [tab, setTab] = useState<DestTab>('local')

  const countryName = useCallback(
    (code: string, fallback: string): string => {
      const v = tCommon(`countries.${code}`)
      return v === `countries.${code}` ? fallback : v
    },
    [tCommon],
  )
  const regionalName = useCallback(
    (code: string, fallback: string): string => {
      const v = tCommon(`regions.${code}`)
      return v === `regions.${code}` ? fallback : v
    },
    [tCommon],
  )

  // Welcome banner: shown once after first signup / Google OAuth.
  const [params, setParams] = useSearchParams()
  const [welcome, setWelcome] = useState(false)
  useEffect(() => {
    if (params.get('welcome') === '1') {
      setWelcome(true)
      params.delete('welcome')
      setParams(params, { replace: true })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const goToCountry = (code: string) => navigate(withLang(`/destinations/${code.toLowerCase()}`, lang))
  const fromFor = (code: string): number | null => (plans ? fromPrice(plans, code) : null)

  // Local destinations: every single-country plan, filtered by search,
  // sorted alphabetically by the name shown in the current locale.
  const localList = useMemo(() => {
    const lower = q.toLowerCase().trim()
    return COUNTRIES.filter((c) => {
      if (!lower) return true
      const tn = countryName(c.code, c.name).toLowerCase()
      return c.name.toLowerCase().includes(lower) || tn.includes(lower)
    }).sort((a, b) =>
      countryName(a.code, a.name).localeCompare(countryName(b.code, b.name), lang),
    )
  }, [q, lang, countryName])

  // Regional bundles, same search + alphabetical treatment.
  const regionalList = useMemo(() => {
    const lower = q.toLowerCase().trim()
    return REGIONAL_PLANS_META.filter((r) => {
      if (!lower) return true
      const tn = regionalName(r.code, r.name).toLowerCase()
      return r.name.toLowerCase().includes(lower) || tn.includes(lower)
    }).sort((a, b) =>
      regionalName(a.code, a.name).localeCompare(regionalName(b.code, b.name), lang),
    )
  }, [q, lang, regionalName])

  const activeList = tab === 'local' ? localList : regionalList

  return (
    <>
      {welcome && (
        <div className="welcome-banner" role="status">
          <div className="welcome-banner__inner">
            <span className="welcome-banner__star" aria-hidden="true">✦</span>
            <span>
              {user?.name
                ? t('welcomeWithName', { name: user.name.split(' ')[0] })
                : t('welcomeFallback')}
            </span>
            <button
              type="button"
              className="welcome-banner__close"
              onClick={() => setWelcome(false)}
              aria-label="Dismiss"
            >
              <Icon name="x" size={11} />
            </button>
          </div>
        </div>
      )}
      <section className="dest-hero">
        <div className="dest-hero-inner">
          <div className="breadcrumb">
            <Link to={withLang('/', lang)} style={{ color: 'var(--ink-3)' }}>
              {tCommon('nav.home')}
            </Link>{' '}
            / <span style={{ color: 'var(--ink)' }}>{t('breadcrumb')}</span>
          </div>
          <h1 className="h1" style={{ fontSize: 48, maxWidth: '16ch', marginTop: 16 }}>
            {t('title')}
          </h1>
          <p className="lede" style={{ marginTop: 14 }}>{t('lede')}</p>

          <div className="dest-credit-chip">
            <span className="dest-credit-chip__star" aria-hidden="true">✦</span>
            {/* <Trans> keeps the <strong> around "10%" in place across all
                8 locales (word order varies), so the number stays bolded. */}
            <Trans i18nKey="creditChip" t={t} />
          </div>

          <div className="dest-search-row">
            <div className="dest-search">
              <Icon name="search" size={18} />
              <input
                placeholder={t('searchPlaceholder')}
                value={q}
                onChange={(e) => setQ(e.target.value)}
              />
              {q && (
                <button className="btn subtle sm" onClick={() => setQ('')}>
                  <Icon name="x" size={12} />
                </button>
              )}
            </div>
          </div>
        </div>
      </section>

      <section className="dest-body">
        <div className="dest-body-inner">
          <div className="plan-tabs" style={{ marginBottom: 28 }}>
            <button
              className={tab === 'local' ? 'active' : ''}
              onClick={() => setTab('local')}
            >
              <span className="dot"></span> {t('tabs.local')}
            </button>
            <button
              className={tab === 'regional' ? 'active' : ''}
              onClick={() => setTab('regional')}
            >
              <span className="dot"></span> {t('tabs.regional')}
            </button>
          </div>

          {tab === 'local' && (
            <div className="feat-list">
              {localList.map((c) => (
                <FeatRow
                  key={c.code}
                  flag={c.flag}
                  name={countryName(c.code, c.name)}
                  sub={(countriesMeta?.[c.code]?.networks ?? c.networks).split(',')[0]}
                  fromCents={fromFor(c.code)}
                  fromLabel={t('fromLabel')}
                  currency={t('currency')}
                  onClick={() => goToCountry(c.code)}
                />
              ))}
            </div>
          )}

          {tab === 'regional' && (
            <div className="feat-list">
              {regionalList.map((r) => (
                <FeatRow
                  key={r.code}
                  flag={r.icon}
                  name={regionalName(r.code, r.name)}
                  sub={r.scope}
                  fromCents={fromFor(r.code)}
                  fromLabel={t('fromLabel')}
                  currency={t('currency')}
                  onClick={() => goToCountry(r.code)}
                />
              ))}
            </div>
          )}

          {activeList.length === 0 && (
            <div style={{ padding: 80, textAlign: 'center', color: 'var(--ink-3)' }}>
              {loading ? t('loading') : t('noResults', { query: q })}
            </div>
          )}
        </div>
      </section>
    </>
  )
}
